import { useState } from 'react';
import { useOportunidades } from '@/api/queries/empresa';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil } from 'lucide-react';
import { OportunidadeForm } from '@/components/OportunidadeForm';
import { DeleteButton } from '@/components/DeleteButton';
import type { Oportunidade } from '@/api/types';

export function TabOportunidades({ empresaId }: { empresaId: string }) {
  const { data, isLoading } = useOportunidades(empresaId);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Oportunidade | null>(null);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(o: Oportunidade) {
    setEditing(o);
    setFormOpen(true);
  }

  if (isLoading) return <Skeleton className="h-32 mt-4" />;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {data?.length ?? 0} oportunidade{(data?.length ?? 0) === 1 ? '' : 's'}
        </h3>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Nova oportunidade
        </button>
      </div>

      {!data || data.length === 0 ? (
        <p className="text-muted-foreground">Sem oportunidades abertas.</p>
      ) : (
        <div className="space-y-3">
          {data.map((o) => (
            <Card key={o.id} className="p-4 relative group">
              <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => openEdit(o)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  aria-label="Editar oportunidade"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <DeleteButton entity="oportunidade" id={o.id} iconOnly />
              </div>
              <div className="flex items-center gap-3 flex-wrap pr-8">
                <Badge style={{ backgroundColor: o.estagio.cor }}>{o.estagio.nome}</Badge>
                {o.servico && <span className="text-sm font-medium">{o.servico.nome}</span>}
                {o.valor_estimado_mensal_brl !== undefined && (
                  <span className="text-sm text-muted-foreground">
                    · R$ {Number(o.valor_estimado_mensal_brl).toLocaleString('pt-BR')}/mês
                  </span>
                )}
                {o.probabilidade !== undefined && (
                  <Badge variant="secondary">{o.probabilidade}%</Badge>
                )}
              </div>
              {o.proximo_movimento_recomendado && (
                <p className="text-sm mt-2">
                  <span className="font-medium">Próximo movimento:</span>{' '}
                  {o.proximo_movimento_recomendado}
                </p>
              )}
              {o.acao_descricao && (
                <p className="text-sm text-muted-foreground mt-1">
                  {o.data_proxima_acao && (
                    <span className="font-mono">
                      [{new Date(o.data_proxima_acao).toLocaleDateString('pt-BR')}]{' '}
                    </span>
                  )}
                  {o.acao_descricao}
                </p>
              )}
              {o.motivo_perdido && (
                <p className="text-sm text-destructive mt-1">Perdido: {o.motivo_perdido}</p>
              )}
              {o.valor_vazamento_quantificado_brl_mes !== undefined && (
                <p className="text-xs text-muted-foreground mt-1">
                  Vazamento estimado: R${' '}
                  {Number(o.valor_vazamento_quantificado_brl_mes).toLocaleString('pt-BR')}/mês
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      <OportunidadeForm
        empresaId={empresaId}
        oportunidade={editing}
        open={formOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
